import { forwardRef, useEffect, useRef, useState } from "react";
import { Loader2, Mail, type LucideIcon } from "lucide-react";

import { useRequestEmailTokenMutation } from "@/hooks/use-auth";
import { getApiError } from "@/lib/api/errors";
import { resolveApiErrorMessage } from "@/lib/api/error-messages";
import { emailRequestSchema } from "@/lib/validations/auth";
import { storage } from "@/lib/storage";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input, type InputProps } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { GoogleSignInButton } from "@/components/auth/google-signin-button";
import { LastUsedTag } from "@/components/auth/last-used-tag";

type View = "choose" | "sent";

const IconInput = forwardRef<
  HTMLInputElement,
  InputProps & { icon: LucideIcon }
>(({ icon: Icon, className, ...props }, ref) => (
  <div className="relative mt-1">
    <Icon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
    <Input ref={ref} className={cn("h-11 pl-10 md:text-base", className)} {...props} />
  </div>
));
IconInput.displayName = "IconInput";

const LINK_EXPIRY_MS = 15 * 60 * 1000;

const RESEND_COOLDOWN_MS = 10 * 1000;

// The magic link also registers new emails. Google uses the rendered button
// only; never One Tap.
export function AuthForm() {
  const [view, setView] = useState<View>("choose");
  const [email, setEmail] = useState("");
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [resent, setResent] = useState(false);
  const [resendReady, setResendReady] = useState(false);
  const [sentAt, setSentAt] = useState(() => Date.now());
  const [nowTs, setNowTs] = useState(() => Date.now());
  const emailInputRef = useRef<HTMLInputElement>(null);
  const [lastLoginMethod] = useState(() => storage.getLastLoginMethod());
  // Read once on mount: it describes something that happened before this form
  // existed, so it must not appear or vanish while the user is typing.
  const [sessionExpired] = useState(() => storage.wasSessionExpired());

  const requestMagic = useRequestEmailTokenMutation();
  // Blocks a second send while one is in flight: isPending only disables the
  // buttons a tick after the send starts, and the Enter handler bypasses them.
  // Every send counts against the per-email rate limit.
  const sendingRef = useRef(false);

  const magicErrorMessage = resolveApiErrorMessage(requestMagic.error, {
    fallback: "Couldn't send your link. Try again.",
  });

  // When rate-limited, resending is pointless (and will just 429 again), so the
  // sent view hides the resend control entirely until the user backs out.
  const isRateLimited = getApiError(requestMagic.error).status === 429;

  const sendError =
    fieldError ?? (requestMagic.isError ? magicErrorMessage : null);

  // The expiry label is per minute, so a 15s tick is plenty; a 1s tick would
  // re-render ~60x/min for nothing. The cooldown has its own one-shot timer so
  // it doesn't depend on the tick. Both restart on every (re)send via sentAt.
  useEffect(() => {
    if (view !== "sent") return;
    const cooldown = window.setTimeout(
      () => setResendReady(true),
      RESEND_COOLDOWN_MS
    );
    const ticker = window.setInterval(() => setNowTs(Date.now()), 15 * 1000);
    return () => {
      window.clearTimeout(cooldown);
      window.clearInterval(ticker);
    };
  }, [view, sentAt]);

  const send = async ({ isResend }: { isResend: boolean }) => {
    if (sendingRef.current) return;
    if (!isResend) {
      const parsed = emailRequestSchema.safeParse({ email });
      if (!parsed.success) {
        setFieldError(parsed.error.issues[0]?.message ?? "Enter a valid email");
        emailInputRef.current?.focus();
        return;
      }
      setFieldError(null);
    }
    setResent(false);
    requestMagic.reset();
    sendingRef.current = true;
    try {
      await requestMagic.mutateAsync({ email });
      if (!isResend) {
        // Recorded on send, not on /auth/verify (which often opens on another
        // device), so the hint is reliable on the browser they return to.
        storage.setLastLoginMethod("email");
      }
      const ts = Date.now();
      setSentAt(ts);
      setNowTs(ts);
      setResendReady(false);
      if (isResend) {
        setResent(true);
      } else {
        setView("sent");
      }
    } catch {
      // Surfaced via requestMagic.error
    } finally {
      sendingRef.current = false;
    }
  };

  const sendMagicLink = () => send({ isResend: false });
  const resend = () => send({ isResend: true });

  const goToChoose = () => {
    setFieldError(null);
    requestMagic.reset();
    setView("choose");
  };

  if (view === "sent") {
    const remainingMs = sentAt + LINK_EXPIRY_MS - nowTs;
    const expired = remainingMs <= 0;
    const minutesLeft = Math.ceil(remainingMs / 60000);
    const resendDisabled = requestMagic.isPending || !resendReady;

    return (
      <div>
        <div className="mb-6">
          <h1 className="text-4xl font-semibold tracking-tight break-words">
            Secure link sent to
          </h1>
          <p className="mt-2 text-2xl text-muted-foreground">
            {email}
          </p>
          <p className="mt-4 text-muted-foreground">
            Check your inbox, we sent you a secure link.
          </p>
          <p className="text-muted-foreground">
            {expired
              ? "The secure link has expired."
              : `It expires in ${minutesLeft} ${
                  minutesLeft === 1 ? "minute" : "minutes"
                }.`}
          </p>
        </div>

        {requestMagic.isError && (
          <p className="mb-4 text-destructive" role="alert">
            {magicErrorMessage}
          </p>
        )}
        {resent && !requestMagic.isError && (
          <p className="mb-4 text-muted-foreground">
            Link sent again, check your inbox.
          </p>
        )}

        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-muted-foreground">
          {!isRateLimited && (
            <>
              <button
                type="button"
                onClick={resend}
                disabled={resendDisabled}
                className={
                  resendDisabled
                    ? "cursor-not-allowed line-through"
                    : "cursor-pointer underline underline-offset-4 hover:text-foreground"
                }
              >
                {requestMagic.isPending ? "Resending…" : "Resend link"}
              </button>
              <span aria-hidden>or</span>
            </>
          )}
          <button
            type="button"
            onClick={goToChoose}
            className="cursor-pointer underline underline-offset-4 hover:text-foreground"
          >
            Use a different email
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-4xl font-semibold tracking-tight">
          Welcome to Appio
        </h1>
        {/* role="status" rather than "alert": a session timing out is a normal
            event, not a failure of anything the user just did. */}
        {sessionExpired && (
          <p role="status" className="mt-1.5 text-base">
            Your session expired. Sign in again to pick up where you left off.
          </p>
        )}
        <p className="mt-1.5 text-muted-foreground">
          Enter your email and we'll send a secure link.<br />No password required.
        </p>
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          sendMagicLink();
        }}
        noValidate
      >
        <div className="space-y-2">
          <Label htmlFor="email" className="text-base">
            Email
          </Label>
          <div className="relative">
            <IconInput
              ref={emailInputRef}
              icon={Mail}
              id="email"
              type="email"
              inputMode="email"
              autoComplete="email"
              placeholder="you@company.com"
              maxLength={255}
              autoFocus
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setFieldError(null);
                requestMagic.reset();
              }}
              onKeyDown={(e) => {
                // Some browsers / password managers swallow the form's implicit
                // "Enter submits" behaviour (e.g. while an autofill dropdown is
                // open), so bind Enter explicitly to guarantee it sends the link.
                // A held key's auto-repeats are dropped, not sent.
                if (e.key === "Enter" && !e.nativeEvent.isComposing) {
                  e.preventDefault();
                  if (!e.repeat) void sendMagicLink();
                }
              }}
              aria-invalid={!!sendError}
              aria-describedby={sendError ? "email-error" : undefined}
            />
            {lastLoginMethod === "email" && <LastUsedTag />}
          </div>
          {/* truncate keeps this to one line so its height matches the spacer at
              the foot of the card; otherwise the vertically centered card would
              re-center and the email field would jump. */}
          {sendError && (
            <p
              id="email-error"
              role="alert"
              className="truncate text-base text-destructive"
            >
              {sendError}
            </p>
          )}
        </div>

        <Button
          type="submit"
          disabled={requestMagic.isPending}
          className="mt-4 w-full cursor-pointer text-base"
        >
          {requestMagic.isPending ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Sending…
            </>
          ) : (
            "Send secure link"
          )}
        </Button>
      </form>

      <div className="my-5 flex items-center gap-3 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        <span className="h-px flex-1 bg-border" />
        or
        <span className="h-px flex-1 bg-border" />
      </div>

      <GoogleSignInButton
        onError={setFieldError}
        lastUsed={lastLoginMethod === "google"}
      />

      {/* Sign-in doubles as sign-up (a new email auto-provisions an account on
          verify), so continuing is where consent is captured; the "sent" view
          is past that point. Links open in a new tab so they never interrupt an
          in-progress sign-in. */}
      <p className="mt-8 text-xs leading-relaxed text-muted-foreground">
        By signing up for an Appio account, you agree to our{" "}
        <a
          href="https://appio.so/legal/terms/"
          target="_blank"
          rel="noopener noreferrer"
          className="hover:text-foreground"
        >
          Terms of Service
        </a>{" "}
        and{" "}
        <a
          href="https://appio.so/legal/privacy/"
          target="_blank"
          rel="noopener noreferrer"
          className="hover:text-foreground"
        >
          Privacy Policy
        </a>
        .
      </p>

      {/* Reserves the error line's height (one text-base line plus its
          space-y-2 gap) so the centered card never changes height. */}
      {!sendError && <div aria-hidden className="h-8" />}
    </div>
  );
}
